import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import axios from "axios";
import {
  ArrowLeft,
  Users,
  Shield,
  AlertTriangle,
  Star,
  TrendingUp,
  CheckCircle,
  XCircle,
  Eye,
  Ban,
  Flame,
  Loader2,
  RefreshCw,
  Zap,
  MessageSquare,
  Trash2,
  Filter,
} from "lucide-react";
import { AltalabLogo } from "../components/AltalabLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [reports, setReports] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [filterType, setFilterType] = useState("all"); // "all", "demo", "real"

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [statsRes, pendingRes, usersRes, reportsRes] = await Promise.all([
        axios.get(`${API}/admin/stats?Authorization=Bearer ${token}`),
        axios.get(`${API}/admin/pending-users?Authorization=Bearer ${token}`),
        axios.get(`${API}/admin/users?Authorization=Bearer ${token}`),
        axios.get(`${API}/admin/reports?Authorization=Bearer ${token}`),
      ]);
      setStats(statsRes.data);
      setPendingUsers(pendingRes.data.users);
      setAllUsers(usersRes.data.users);
      setReports(reportsRes.data.reports);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const updateUser = async (userId, updates) => {
    try {
      await axios.put(
        `${API}/admin/users/${userId}?Authorization=Bearer ${token}`,
        updates
      );
      toast.success("Usuário atualizado");
      fetchAllData();
    } catch (error) {
      toast.error("Erro ao atualizar usuário");
    }
  };

  const updateReport = async (reportId, status) => {
    try {
      await axios.put(
        `${API}/admin/reports/${reportId}?status=${status}&Authorization=Bearer ${token}`
      );
      toast.success("Denúncia atualizada");
      fetchAllData();
    } catch (error) {
      toast.error("Erro ao atualizar denúncia");
    }
  };

  const openUserDetail = (user) => {
    setSelectedUser(user);
    setShowUserModal(true);
  };

  const confirmDeleteUser = (user) => {
    setUserToDelete(user);
    setShowDeleteDialog(true);
  };

  const deleteUser = async () => {
    if (!userToDelete) return;
    
    setDeleting(true);
    try {
      await axios.delete(
        `${API}/admin/users/${userToDelete.id}?Authorization=Bearer ${token}`
      );
      toast.success(`Usuário ${userToDelete.display_name} excluído com sucesso`);
      setShowDeleteDialog(false);
      setUserToDelete(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao excluir usuário");
    } finally {
      setDeleting(false);
    }
  };

  const isDemo = (user) => user.email?.endsWith("@demo.com");

  const filteredUsers = allUsers.filter((user) => {
    if (filterType === "demo") return isDemo(user);
    if (filterType === "real") return !isDemo(user);
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#080808] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080808]">
      {/* Header */}
      <header className="sticky top-0 z-50 px-4 py-4 bg-black/80 backdrop-blur-md border-b border-white/5">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/profile")}
              className="text-white/70"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <AltalabLogo />
              <div className="border-l border-white/20 pl-3">
                <h1 className="font-heading text-lg font-bold">Admin</h1>
                <p className="text-xs text-white/50">Centro de Comando</p>
              </div>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={fetchAllData}
            className="text-white/70"
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-5 h-5" />
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            {
              label: "Total Usuários",
              value: stats?.total_users || 0,
              icon: <Users className="w-5 h-5" />,
              color: "text-[#FD5564]",
            },
            {
              label: "Verificados",
              value: stats?.verified_users || 0,
              icon: <CheckCircle className="w-5 h-5" />,
              color: "text-emerald-400",
            },
            {
              label: "Pendentes",
              value: stats?.pending_verification || 0,
              icon: <Eye className="w-5 h-5" />,
              color: "text-yellow-400",
            },
            {
              label: "Demo",
              value: stats?.demo_users || 0,
              icon: <Users className="w-5 h-5" />,
              color: "text-violet-400",
            },
            {
              label: "Denúncias",
              value: stats?.pending_reports || 0,
              icon: <AlertTriangle className="w-5 h-5" />,
              color: "text-red-400",
            },
            {
              label: "Matches",
              value: stats?.total_matches || 0,
              icon: <TrendingUp className="w-5 h-5" />,
              color: "text-[#FD5564]",
            },
            {
              label: "VIP",
              value: stats?.vip_users || 0,
              icon: <Star className="w-5 h-5" />,
              color: "text-white",
            },
            {
              label: "Agenciados",
              value: stats?.agency_users || 0,
              icon: <Flame className="w-5 h-5" />,
              color: "text-[#FD5564]",
            },
          ].map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="glass-card rounded-2xl p-4"
              data-testid={`stat-card-${index}`}
            >
              <div className={`${stat.color} mb-2`}>{stat.icon}</div>
              <div className="text-2xl font-bold">{stat.value}</div>
              <div className="text-xs text-white/50">{stat.label}</div>
            </motion.div>
          ))}
        </div>

        {/* Tabs */}
        <Tabs defaultValue="pending" className="space-y-6">
          <TabsList className="bg-white/5 border border-white/10">
            <TabsTrigger value="pending" data-testid="tab-pending">
              Verificação ({pendingUsers.length})
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">
              Usuários ({allUsers.length})
            </TabsTrigger>
            <TabsTrigger value="reports" data-testid="tab-reports">
              Denúncias ({reports.filter((r) => r.status === "pending").length})
            </TabsTrigger>
          </TabsList>

          {/* Pending Verification */}
          <TabsContent value="pending">
            <div className="glass-card rounded-2xl overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-white/70">Usuário</TableHead>
                    <TableHead className="text-white/70">Redes</TableHead>
                    <TableHead className="text-white/70">Foto Verificação</TableHead>
                    <TableHead className="text-white/70">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingUsers.map((user) => (
                    <TableRow key={user.id} className="border-white/5">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full overflow-hidden bg-white/10">
                            {user.profile_photos?.[0] ? (
                              <img
                                src={`${process.env.REACT_APP_BACKEND_URL}${user.profile_photos[0]}`}
                                alt={user.display_name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-white/30">
                                <Users className="w-5 h-5" />
                              </div>
                            )}
                          </div>
                          <div>
                            <div className="font-medium">{user.display_name}</div>
                            <div className="text-sm text-white/50">@{user.username}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {user.social_links?.instagram && (
                            <Badge variant="outline" className="text-xs">IG</Badge>
                          )}
                          {user.social_links?.twitter && (
                            <Badge variant="outline" className="text-xs">TW</Badge>
                          )}
                          {user.social_links?.tiktok && (
                            <Badge variant="outline" className="text-xs">TT</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {user.verification_photo ? (
                          <a
                            href={`${process.env.REACT_APP_BACKEND_URL}${user.verification_photo}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#FD5564] hover:text-[#FF7854] text-sm"
                          >
                            Ver foto
                          </a>
                        ) : (
                          <span className="text-white/30 text-sm">Não enviada</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() =>
                              updateUser(user.id, { verification_status: "verified" })
                            }
                            className="bg-emerald-600 hover:bg-emerald-500 h-8"
                            data-testid={`approve-${user.id}`}
                          >
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() =>
                              updateUser(user.id, { verification_status: "rejected" })
                            }
                            className="h-8"
                            data-testid={`reject-${user.id}`}
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openUserDetail(user)}
                            className="h-8"
                            data-testid={`view-${user.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {pendingUsers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-white/50 py-8">
                        Nenhum usuário pendente
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* All Users */}
          <TabsContent value="users">
            {/* Filter Bar */}
            <div className="flex items-center gap-3 mb-4">
              <Filter className="w-4 h-4 text-white/50" />
              <div className="flex gap-2">
                {[
                  { value: "all", label: "Todos" },
                  { value: "demo", label: "Demo" },
                  { value: "real", label: "Reais" },
                ].map((filter) => (
                  <Button
                    key={filter.value}
                    size="sm"
                    variant={filterType === filter.value ? "default" : "ghost"}
                    onClick={() => setFilterType(filter.value)}
                    className={
                      filterType === filter.value
                        ? "bg-violet-600 hover:bg-violet-500"
                        : "text-white/50"
                    }
                    data-testid={`filter-${filter.value}`}
                  >
                    {filter.label}
                  </Button>
                ))}
              </div>
              <span className="text-white/50 text-sm ml-auto">
                {filteredUsers.length} usuários
              </span>
            </div>

            <div className="glass-card rounded-2xl overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-white/70">Usuário</TableHead>
                    <TableHead className="text-white/70">Status</TableHead>
                    <TableHead className="text-white/70">Trust Score</TableHead>
                    <TableHead className="text-white/70">Badges</TableHead>
                    <TableHead className="text-white/70">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((user) => (
                    <TableRow key={user.id} className="border-white/5">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full overflow-hidden bg-white/10">
                            {user.profile_photos?.[0] ? (
                              <img
                                src={`${process.env.REACT_APP_BACKEND_URL}${user.profile_photos[0]}`}
                                alt={user.display_name}
                                className="w-full h-full object-cover"
                              />
                            ) : user.photos?.[0] ? (
                              <img
                                src={user.photos[0]}
                                alt={user.display_name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-white/30">
                                <Users className="w-5 h-5" />
                              </div>
                            )}
                          </div>
                          <div>
                            <div className="font-medium flex items-center gap-2">
                              {user.display_name}
                              {isDemo(user) && (
                                <Badge className="bg-violet-600/30 text-violet-300 text-[10px]">
                                  DEMO
                                </Badge>
                              )}
                            </div>
                            <div className="text-sm text-white/50">@{user.username}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={
                            user.verification_status === "verified"
                              ? "border-emerald-500/30 text-emerald-400"
                              : user.verification_status === "pending"
                              ? "border-yellow-500/30 text-yellow-400"
                              : "border-red-500/30 text-red-400"
                          }
                        >
                          {user.verification_status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-yellow-400">
                          {user.trust_score?.toFixed(1) || "0.0"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {user.is_vip && (
                            <Badge className="bg-white text-black text-xs">VIP</Badge>
                          )}
                          {user.is_agency && (
                            <Badge className="bg-gradient-to-r from-[#FD5564] to-[#FF7854] text-xs">Agency</Badge>
                          )}
                          {user.is_boosted && (
                            <Badge className="bg-emerald-600 text-xs">Boost</Badge>
                          )}
                          {user.is_shadowbanned && (
                            <Badge variant="destructive" className="text-xs">
                              Shadow
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              updateUser(user.id, { is_vip: !user.is_vip })
                            }
                            className={`h-8 ${
                              user.is_vip ? "text-white" : "text-white/50"
                            }`}
                            title="VIP"
                            data-testid={`vip-${user.id}`}
                          >
                            <Star className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              updateUser(user.id, { is_agency: !user.is_agency })
                            }
                            className={`h-8 ${
                              user.is_agency ? "text-[#FD5564]" : "text-white/50"
                            }`}
                            title="Agência"
                            data-testid={`agency-${user.id}`}
                          >
                            <Shield className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              updateUser(user.id, {
                                is_boosted: !user.is_boosted,
                                boost_multiplier: 3,
                              })
                            }
                            className={`h-8 ${
                              user.is_boosted ? "text-emerald-400" : "text-white/50"
                            }`}
                            title="Boost 3x"
                            data-testid={`boost-${user.id}`}
                          >
                            <Zap className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              updateUser(user.id, {
                                is_shadowbanned: !user.is_shadowbanned,
                              })
                            }
                            className={`h-8 ${
                              user.is_shadowbanned ? "text-red-400" : "text-white/50"
                            }`}
                            title="Shadowban"
                            data-testid={`shadowban-${user.id}`}
                          >
                            <Ban className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => confirmDeleteUser(user)}
                            className="h-8 text-white/50 hover:text-red-400 hover:bg-red-500/10"
                            title="Excluir"
                            data-testid={`delete-${user.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Reports */}
          <TabsContent value="reports">
            <div className="glass-card rounded-2xl overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-white/70">Denunciante</TableHead>
                    <TableHead className="text-white/70">Denunciado</TableHead>
                    <TableHead className="text-white/70">Motivo</TableHead>
                    <TableHead className="text-white/70">Status</TableHead>
                    <TableHead className="text-white/70">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reports.map((report) => (
                    <TableRow key={report.id} className="border-white/5">
                      <TableCell>
                        <div className="text-sm">
                          {report.reporter?.display_name || "Desconhecido"}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {report.reported_user?.display_name || "Desconhecido"}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{report.reason}</div>
                        <div className="text-xs text-white/50 truncate max-w-[200px]">
                          {report.description}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={
                            report.status === "pending"
                              ? "border-yellow-500/30 text-yellow-400"
                              : report.status === "resolved"
                              ? "border-emerald-500/30 text-emerald-400"
                              : "border-white/30 text-white/70"
                          }
                        >
                          {report.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => updateReport(report.id, "resolved")}
                            className="bg-emerald-600 hover:bg-emerald-500 h-8"
                            data-testid={`resolve-${report.id}`}
                          >
                            Resolver
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => updateReport(report.id, "dismissed")}
                            className="h-8"
                            data-testid={`dismiss-${report.id}`}
                          >
                            Ignorar
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {reports.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-white/50 py-8">
                        Nenhuma denúncia
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* User Detail Modal */}
      <Dialog open={showUserModal} onOpenChange={setShowUserModal}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white font-heading">
              Detalhes do Usuário
            </DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4 mt-4">
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-2xl overflow-hidden bg-white/10">
                  {selectedUser.profile_photos?.[0] ? (
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${selectedUser.profile_photos[0]}`}
                      alt={selectedUser.display_name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Users className="w-8 h-8 text-white/30" />
                    </div>
                  )}
                </div>
                <div>
                  <h3 className="font-semibold text-lg">{selectedUser.display_name}</h3>
                  <p className="text-white/50">@{selectedUser.username}</p>
                  <p className="text-white/50 text-sm">{selectedUser.email}</p>
                </div>
              </div>

              {selectedUser.verification_photo && (
                <div>
                  <h4 className="text-sm font-semibold text-white/70 mb-2">
                    Foto de Verificação
                  </h4>
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${selectedUser.verification_photo}`}
                    alt="Verification"
                    className="max-h-64 rounded-xl"
                  />
                </div>
              )}

              {selectedUser.bio && (
                <div>
                  <h4 className="text-sm font-semibold text-white/70 mb-2">Bio</h4>
                  <p className="text-white/80 text-sm">{selectedUser.bio}</p>
                </div>
              )}

              {Object.keys(selectedUser.social_links || {}).length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-white/70 mb-2">
                    Redes Sociais
                  </h4>
                  <div className="space-y-1">
                    {Object.entries(selectedUser.social_links).map(([key, value]) => (
                      <a
                        key={key}
                        href={value}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-violet-400 hover:text-violet-300 text-sm truncate"
                      >
                        {key}: {value}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-[#0a0a0a] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              Confirmar Exclusão
            </AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              Tem certeza que deseja excluir o usuário{" "}
              <strong className="text-white">{userToDelete?.display_name}</strong>?
              {isDemo(userToDelete || {}) && (
                <span className="block mt-2 text-violet-400">
                  Este é um usuário de demonstração.
                </span>
              )}
              <span className="block mt-2 text-red-400">
                Esta ação não pode ser desfeita. Todos os dados do usuário serão removidos.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="bg-white/5 border-white/10 text-white hover:bg-white/10"
              disabled={deleting}
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteUser}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-500 text-white"
              data-testid="confirm-delete-btn"
            >
              {deleting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminPage;
